import { QuitConfirmModal } from '@/src/components/ui/molecules/EventTemplate';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Location } from 'history';
import React, { useEffect, useRef, useState } from 'react';
import { Prompt, useHistory } from 'react-router-dom';

interface LeavePageGuardProps {
  when: boolean;
}

/**
 * While `when` is true, asks for confirmation before leaving the page. In-app navigation shows the unsaved
 * changes modal; closing or reloading the tab shows the browser's own prompt.
 */
const LeavePageGuard: React.FC<LeavePageGuardProps> = ({ when }) => {
  const history = useHistory();
  const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
  const leaveConfirmed = useRef(false);

  useEffect(() => {
    if (!when) {
      return;
    }

    // Browsers ignore custom text here and show their own generic message.
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  const handleNavigation = (location: Location) => {
    if (leaveConfirmed.current) {
      return true;
    }

    setPendingLocation(location);
    return false;
  };

  const handleLeave = () => {
    if (!pendingLocation) {
      return;
    }

    setPendingLocation(null);
    leaveConfirmed.current = true;
    history.push(pendingLocation);
    leaveConfirmed.current = false;
  };

  return (
    <>
      <Prompt when={when} message={handleNavigation} />
      <QuitConfirmModal
        visible={pendingLocation !== null}
        message={i18nKeys.eventTemplates.quitModal.unsavedChangesMessage}
        onOkClick={handleLeave}
        onDismiss={() => setPendingLocation(null)}
      />
    </>
  );
};

export default LeavePageGuard;
